import { Outlet, useNavigate } from '@tanstack/react-router';
import {
  Bell,
  Building2,
  Check,
  CheckCheck,
  CircleDollarSign,
  ChevronRight,
  GitBranch,
  LogOut,
  PlugZap,
  Search,
  Settings,
  Shield,
  Sparkles,
  X,
  Pin,
  PinOff,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { NavGroupWithSubmenu } from '~/components/common/app-shell/nav-group-with-submenu';
import { NavItem } from '~/components/common/app-shell/nav-item';
import { ScreenGuard } from '~/components/common/screen-guard/screen-guard';
import { ShowCostMenuItem } from '~/components/common/show-cost-menu-item';
import { TrialModeBanner } from '~/components/common/trial-banner/trial-mode-banner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Input } from '~/components/ui/input';
import { Separator } from '~/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { flagKeyForCode } from '~/constants/config/feature-gates';
import { performLogout } from '~/features/auth/utils/perform-logout';
import { usePendingApproverEstimates } from '~/features/cost-estimate/hooks/use-cost-estimate';
import { useWakeAllServices } from '~/features/integrations/hooks/use-integration-health';
import { NotificationRow } from '~/features/notifications/components/notification-row';
import {
  useInbox,
  useMarkAllInboxRead,
  useMarkNotificationRead,
  useUnreadCount,
} from '~/features/notifications/hooks/use-notifications';
import type { InboxNotification } from '~/features/notifications/types/notifications.api.types';
import { getNotificationRoute } from '~/features/notifications/utils/notification-route';
import { ImpersonationBanner } from '~/features/platform/components/users/impersonation-banner';
import {
  isProductCatalogNav,
  useHasProductCatalog,
} from '~/features/product-catalog/hooks/use-catalog-connections';
import { useNavigation } from '~/hooks/use-navigation';
import { useSubsidiaryScreens } from '~/hooks/use-subsidiary-screens';
import { useCostAccess } from '~/hooks/useCanSeeCost';
import { useTenantSwitch } from '~/hooks/useTenantSwitch';
import { useAuthStore } from '~/store/auth.store';
import { usePreferencesStore } from '~/store/preferences.store';
import { useTenantStore } from '~/store/tenant.store';
import { useTrialStore } from '~/store/trial.store';
import type { NavGroupApi } from '~/types/navigation.types';
import { canAccessNavPath } from '~/utils/access';
import { cn } from '~/utils/cn';
import { extractApiError } from '~/utils/extract-api-error';
import { getIcon } from '~/utils/icon-map';

/** Backend scope string for super-admin-only nav groups (see `SYSTEM_ADMIN_SCOPE`). */
const SYSTEM_ADMIN_SCOPE_CODE = 'SYSTEM_ADMIN';

/** Nav groups pinned below the separator (platform admin, integrations, config),
 *  in the order they render — single source of truth. Integrations is pinned
 *  here (rather than in the dynamic section) so it sits directly after Platform
 *  Administration.
 *
 *  A path absent from this list renders in the *dynamic* section ABOVE the
 *  separator instead, in whatever order the API returned it — which is why
 *  Explorer is listed here. It is an admin/diagnostic surface, and
 *  agentic-identity-service already seeds it with that intent
 *  (`20260805000000_seed_explorer_screens` gives it sort_order 3, "between
 *  integrations (2) and subsidiary_settings"); only this list had not caught up. */
const PINNED_ADMIN_ORDER = [
  '/platform',
  '/taxonomy',
  '/integrations',
  '/deliveries',
  '/schedules',
  '/coverage',
  '/explorer',
  '/configuration',
  '/subsidiary',
  '/subsidiary-preferences',
  '/workflow-runs',
] as const;
const PINNED_ADMIN_PATHS = new Set<string>(PINNED_ADMIN_ORDER);

/** Position of a pinned path in the fixed order above; unknown paths sort last. */
function pinnedOrderIndex(path: string): number {
  const i = PINNED_ADMIN_ORDER.indexOf(path as (typeof PINNED_ADMIN_ORDER)[number]);
  return i === -1 ? PINNED_ADMIN_ORDER.length : i;
}

/**
 * Hardcoded Taxonomy Studio entry, used only while `GET /screens/navigation` does not
 * yet return identity-service's SYSTEM_ADMIN `taxonomy_studio` group (same path, code
 * and icon as that seed, so swapping to the backend group never renders it twice).
 * Shown only to Lily AI platform admins via `canAccessNavPath('/taxonomy')`.
 */
const TAXONOMY_STUDIO_FALLBACK_NAV: NavGroupApi = {
  id: 'nav-group-taxonomy-studio-fallback',
  title: 'Taxonomy Studio',
  code: 'taxonomy_studio',
  path: '/taxonomy',
  icon: 'library-big',
  module: 'taxonomy',
  scope: SYSTEM_ADMIN_SCOPE_CODE,
  items: [],
};

/**
 * Schedules across subsidiaries (read-only), pinned after Deliveries. Hardcoded the same way as
 * the Taxonomy Studio fallback until identity-service seeds a SYSTEM_ADMIN `schedules` screen
 * (same path, so a seeded group replaces this one rather than rendering twice). Shown only to
 * Lily AI platform admins via `canAccessNavPath('/schedules')`.
 */
const SCHEDULES_FALLBACK_NAV: NavGroupApi = {
  id: 'nav-group-schedules-fallback',
  title: 'Schedules',
  code: 'schedules',
  path: '/schedules',
  icon: 'calendar-clock',
  module: 'schedules',
  scope: SYSTEM_ADMIN_SCOPE_CODE,
  items: [],
};

/**
 * Coverage across subsidiaries (read-only), pinned after Schedules and hardcoded the same way until
 * identity-service seeds a SYSTEM_ADMIN `coverage` screen. Shown only to Lily AI platform admins via
 * `canAccessNavPath('/coverage')`.
 */
const COVERAGE_FALLBACK_NAV: NavGroupApi = {
  id: 'nav-group-coverage-fallback',
  title: 'Coverage',
  code: 'coverage',
  path: '/coverage',
  icon: 'gauge',
  module: 'coverage',
  scope: SYSTEM_ADMIN_SCOPE_CODE,
  items: [],
};

/** Hardcoded pinned groups, each used only while identity does not serve its path. */
const FALLBACK_PINNED_NAV: NavGroupApi[] = [
  TAXONOMY_STUDIO_FALLBACK_NAV,
  SCHEDULES_FALLBACK_NAV,
  COVERAGE_FALLBACK_NAV,
];

/**
 * Catalog Enrichment's sidebar entry, added in code because `GET /screens/navigation` doesn't
 * always return it. Not flag-gated, but hidden like any other group when the screen is
 * switched off for the subsidiary; if the backend sends the same path, the backend group wins.
 */
const CATALOG_ENRICHMENT_NAV: NavGroupApi = {
  id: 'nav-group-catalog-enrichment',
  title: 'Catalog Enrichment',
  code: 'catalog_enrichment',
  path: '/catalog-enrichment',
  icon: 'package-search',
  module: 'catalog-enrichment',
  scope: '',
  items: [],
};

/** Adds Catalog Enrichment right after Catalog (preview) — or last when that isn't listed —
 *  unless the backend already returns it. */
function withCatalogEnrichment(groups: NavGroupApi[]): NavGroupApi[] {
  if (groups.some((g) => g.path === CATALOG_ENRICHMENT_NAV.path)) return groups;
  const at = groups.findIndex((g) => g.path === '/catalog-v2');
  if (at === -1) return [...groups, CATALOG_ENRICHMENT_NAV];
  return [...groups.slice(0, at + 1), CATALOG_ENRICHMENT_NAV, ...groups.slice(at + 1)];
}

/** Nav groups returned by GET /screens/navigation that we intentionally hide
 *  from the sidebar (product decision). The route stays reachable by URL.
 *  Currently empty — Product Catalog now ships, gated on connections. */
const HIDDEN_NAV_PATHS = new Set<string>();

/** Backend scope marking a nav group as super-admin only (same sections the
 *  `requireSystemAdmin` route guards protect). Groups carrying this scope are
 *  hidden from non-super-admins so the sidebar never advertises a section that
 *  would only redirect them away. */
const SYSTEM_ADMIN_SCOPE = SYSTEM_ADMIN_SCOPE_CODE;

/** Nav groups hidden while the active subsidiary is on a trial contract. Feed
 *  Audit is an internal tool a trial subsidiary must not see. */
const TRIAL_HIDDEN_PATHS = new Set<string>(['/feed-audit']);

/**
 * Extra URL prefixes that keep a nav group highlighted, keyed by the group's own
 * path. Some flows are entered from one section but routed under a different
 * root, so prefix-matching the group path alone leaves the sidebar showing no
 * active item at all while the user is deep in that flow.
 */
const NAV_EXTRA_ACTIVE_PATHS: Record<string, string[]> = {
  // Content-enrichment (product selection → batch review → delivery) is launched
  // from Catalog Operations but lives under `/enrichments/{google,meta}/*`.
  '/catalog-operations': ['/enrichments'],
};

/** Sidebar sections still in preview, keyed by the group's path → the `common:navTag.*` pill
 *  shown after their label. */
const NAV_TAGS: Record<string, 'preview'> = {
  '/catalog-enrichment': 'preview',
};

/**
 * Primary layout for all authenticated routes.
 * Sidebar + full-width content area.
 */
export function CopilotShell() {
  const { t } = useTranslation('common');
  /** The preview pill for a sidebar section, or nothing when it isn't in preview. */
  const navTagFor = (path: string): string | undefined => {
    const tag = NAV_TAGS[path];
    return tag ? t(`navTag.${tag}`) : undefined;
  };
  const user = useAuthStore((s) => s.user);
  // Gates the super-admin-only nav groups below — non-super-admins never see them.
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  // Cost approvals is not a backend-navigation screen: it shows only to the cost approvers
  // (workflow-service's COST_APPROVAL_APPROVER_EMAILS, read via approver-status). Approvers see
  // cost whatever their domain; other customers never do.
  const { isApprover: isCostApprover } = useCostAccess();
  const { data: pendingCostApprovals } = usePendingApproverEstimates({ enabled: isCostApprover });
  const pendingCostCount = pendingCostApprovals?.length ?? 0;

  // A trial subsidiary runs on a TRIAL_POC contract with no LIVE one (same signal
  // the trial banner uses). Gate on `isLoaded` so nothing is hidden before the
  // contracts resolve. Drives Feed Audit's trial visibility below.
  const enrolledContracts = useTrialStore((s) => s.enrolledContracts);
  const trialLoaded = useTrialStore((s) => s.isLoaded);
  const contractTypes = Object.values(enrolledContracts);
  const isTrialSubsidiary =
    trialLoaded &&
    contractTypes.some((c) => c === 'TRIAL_POC') &&
    !contractTypes.some((c) => c === 'LIVE');

  // Screens switched off for the active subsidiary on Navigation Access (super admins exempt).
  const subsidiaryScreens = useSubsidiaryScreens();

  /** Shared visibility gate for both nav passes: drops intentionally-hidden
   *  paths, backend-flagged system-admin sections for non-admins, trial-hidden
   *  sections, and — critically — any path the user's role/permissions don't
   *  allow. The last check goes through `canAccessNavPath`, the same policy the
   *  route `beforeLoad` guards enforce, so the menu never shows an item that
   *  would only bounce the user to Access Denied. Screens switched off for the
   *  subsidiary on Navigation Access are hidden too (super admins exempt). */
  const isGroupVisible = (path: string, scope: string): boolean => {
    if (HIDDEN_NAV_PATHS.has(path)) return false;
    if (!subsidiaryScreens.isPathEnabled(path)) return false;
    if (!isSuperAdmin && scope === SYSTEM_ADMIN_SCOPE) return false;
    if (isTrialSubsidiary && TRIAL_HIDDEN_PATHS.has(path)) return false;
    if (!canAccessNavPath(path, user)) return false;
    return true;
  };

  const navigate = useNavigate();
  const { data: navigationData } = useNavigation();
  const mainNavGroups = navigationData ? withCatalogEnrichment(navigationData.groups) : undefined;
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  // Controlled state for the notification dropdown so we can explicitly close
  // it before navigating — Radix doesn't auto-close on clicks of non-Item
  // children (e.g. the "Show all notifications" button or notification rows).
  const [notifOpen, setNotifOpen] = useState(false);
  // Hover-driven rail expansion: the icon rail widens into a labelled panel on
  // hover so users can read each option's name inline (replacing the per-icon
  // tooltips). Overlays the content rather than pushing it.
  const sidebarPinned = usePreferencesStore((s) => s.sidebarPinned);
  const setSidebarPinned = usePreferencesStore((s) => s.setSidebarPinned);
  // Starts expanded when pinned so a reload doesn't need a hover to restore it.
  const [expanded, setExpanded] = useState(sidebarPinned);

  // Feature-flag maps drive outer-rail visibility: a group whose `code` is in
  // the FEATURE_GATES registry must have its flag enabled to render. Same
  // semantics as `nav-main.tsx` so the rails stay in lockstep.
  const authFlags = useAuthStore((s) => s.user?.featureFlags);
  const tenantFlags = useTenantStore((s) => s.config?.featureFlags);

  // Connection-based (not flag-based) visibility for the Product Catalog group:
  // it only surfaces once the tenant has an importable shopping-feed catalog.
  const hasProductCatalog = useHasProductCatalog();

  // Source tenants from the auth store — populated from /auth/me's
  // `availableSubsidiaries` so the dropdown surfaces every subsidiary the user
  // can access (across organizations), not just those returned by /subsidiaries.
  const tenants = user?.accessibleTenants ?? [];
  const { switchTenant, isSwitching } = useTenantSwitch();
  const activeTenantId = user?.activeTenantId;
  const activeTenant = tenants.find((t) => t.id === activeTenantId);
  const activeTenantName = activeTenant?.name ?? '';
  // Lets a super admin jump straight back to the internal system subsidiary
  // without hunting through the switcher list; hides itself once already there.
  const systemTenant = tenants.find((t) => t.isSystem);
  const showSystemQuickSwitch = isSuperAdmin && !!systemTenant && !activeTenant?.isSystem;
  // Manual "boot everything" trigger for super admins — all backend services
  // and agents are serverless and cold-start on first call, so this lets an
  // admin warm the whole fleet at once instead of eating that latency later.
  const wakeAllServices = useWakeAllServices();
  const tenantInitials = activeTenantName
    ? activeTenantName
        .split(' ')
        .map((w) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'BU';
  const hasMultipleSubsidiaries = tenants.length > 1;

  // Controlled state for the tenant dropdown so we can reset the search query on close.
  const [tenantDropdownOpen, setTenantDropdownOpen] = useState(false);
  const [tenantSearch, setTenantSearch] = useState('');
  // Ref to the subsidiary search input so it can be focused on open (overriding
  // Radix's default first-item focus) and re-focused after the search is cleared.
  const tenantSearchRef = useRef<HTMLInputElement>(null);

  const handleTenantDropdownChange = (next: boolean) => {
    setTenantDropdownOpen(next);
    if (!next) setTenantSearch('');
  };

  /** Show a search input once the list exceeds this threshold. */
  const TENANT_SEARCH_THRESHOLD = 5;
  const showTenantSearch = tenants.length > TENANT_SEARCH_THRESHOLD;
  const tenantQuery = tenantSearch.trim().toLowerCase();
  const filteredTenants = [
    ...(showTenantSearch && tenantQuery
      ? tenants.filter((t) => {
          // One haystack per tenant — name, slug and subsidiary id joined with
          // ':' — so a pasted UUID, a slug, a name, or a combined "name:slug"
          // string all hit. Every whitespace-separated term must appear in it,
          // which lets "bombas sajin" narrow across two fields at once.
          const haystack = `${t.name}:${t.slug}:${t.id}`.toLowerCase();
          return tenantQuery.split(/\s+/).every((term) => haystack.includes(term));
        })
      : tenants),
  ].sort((a, b) => {
    if (a.id === activeTenantId) return -1;
    if (b.id === activeTenantId) return 1;
    return 0;
  });

  const handleSwitchTenant = async (tenantId: string) => {
    if (tenantId === activeTenantId || isSwitching) return;
    await switchTenant(tenantId);
  };

  // Autofocus the subsidiary search on open. DropdownMenu focuses its first
  // item on open, so we defer one frame past Radix's own focus to land the
  // caret in the input instead — only when the search box is actually shown.
  useEffect(() => {
    if (!tenantDropdownOpen || !showTenantSearch) return;
    const id = requestAnimationFrame(() => tenantSearchRef.current?.focus());
    return () => {
      cancelAnimationFrame(id);
    };
  }, [tenantDropdownOpen, showTenantSearch]);

  const { data: inboxData } = useInbox();
  const { data: unreadCountData } = useUnreadCount();
  const { mutate: markAllRead, isPending: markingRead } = useMarkAllInboxRead();
  const { mutate: markRead } = useMarkNotificationRead();
  const inboxNotifications = inboxData?.notifications ?? [];
  const unreadCount = unreadCountData ?? 0;
  const previewNotifications = inboxNotifications.slice(0, 5);

  // Unread-count display mode set on the Notifications settings page:
  // `yes-count` → numeric badge, `yes-badge` → dot indicator, `no` → nothing.
  const showUnreadCountMode = usePreferencesStore((s) => s.showUnreadCount);
  const hasUnread = unreadCount > 0;
  const showNumericBadge = hasUnread && showUnreadCountMode === 'yes-count';
  const showDotBadge = hasUnread && showUnreadCountMode === 'yes-badge';

  const handleNotificationNavigate = (notification: InboxNotification) => {
    if (!notification.is_read) markRead(notification.id);
    setNotifOpen(false);
    void navigate({ to: getNotificationRoute(notification) });
  };

  /** Closes the notification dropdown then navigates to the full inbox view. */
  const handleShowAllNotifications = () => {
    setNotifOpen(false);
    void navigate({ to: '/notification' });
  };

  /** Closes the notification dropdown then deep-links to notification preferences. */
  const handleOpenNotificationSettings = () => {
    setNotifOpen(false);
    void navigate({ to: '/settings/notification' });
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await performLogout();
    void navigate({ to: '/login' });
  };

  return (
    <div className="relative flex h-dvh overflow-hidden">
      {/* Spacer reserves the collapsed rail width (w-18) so page content never
          shifts when the overlay rail expands on hover. */}
      {/* Spacer reserves the rail's footprint. Unpinned it holds the collapsed
          width so a hover-peek floats over the content without shifting it;
          pinned, the rail is permanently expanded, so the spacer widens and the
          content lays out beside it instead of underneath. */}
      <div
        aria-hidden="true"
        className={cn(
          'shrink-0 transition-[width] duration-200 ease-out',
          sidebarPinned ? 'w-65' : 'w-18',
        )}
      />
      {/* Sidebar — icon rail that expands into a labelled panel on hover.
          Absolutely positioned so it floats over the content instead of
          pushing it; width animates between collapsed (w-18) and expanded. */}
      <aside
        aria-label="Site navigation"
        onMouseEnter={() => {
          setExpanded(true);
        }}
        onMouseLeave={() => {
          // A pinned rail stays open; only hover-expansion snaps back.
          if (!sidebarPinned) setExpanded(false);
        }}
        className={cn(
          'bg-sidebar absolute inset-y-0 left-0 z-40 flex h-dvh flex-col overflow-hidden border-r transition-[width] duration-200 ease-out',
          expanded ? 'w-65 shadow-xl' : 'w-18',
        )}
      >
        {/* Subsidiary badge */}
        <div className="flex shrink-0 items-center pt-5 pb-4">
          {/* Fixed 72px zone keeps the badge in the same position when expanded. */}
          <div className="flex w-18 shrink-0 items-center justify-center">
            {hasMultipleSubsidiaries ? (
              <DropdownMenu open={tenantDropdownOpen} onOpenChange={handleTenantDropdownChange}>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="bg-nav-badge-bg flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl text-xs font-semibold text-white"
                      >
                        {tenantInitials}
                      </button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  {!expanded && (
                    <TooltipContent side="right" sideOffset={8}>
                      {activeTenantName || 'Subsidiary'}
                    </TooltipContent>
                  )}
                </Tooltip>
                <DropdownMenuContent
                  side="right"
                  align="start"
                  sideOffset={12}
                  className="w-72 rounded-xl p-0"
                >
                  <div className="flex items-start justify-between gap-2 px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
                        Switch Subsidiary
                      </p>
                      <p className="text-muted-foreground mt-0.5 truncate text-xs">
                        {activeTenantName}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {/* Super-admin-only: wake every backend service and agent
                          at once, so cold-starts don't hit the admin's next
                          real action. */}
                      {isSuperAdmin && (
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              isLoading={wakeAllServices.isPending}
                              aria-label="Wake all services"
                              onClick={(e) => {
                                e.stopPropagation();
                                wakeAllServices.mutate(undefined, {
                                  onSuccess: () => {
                                    toast.success('Waking all services and agents');
                                  },
                                  onError: (err) => {
                                    toast.error(extractApiError(err, 'Failed to wake services'));
                                  },
                                });
                              }}
                              className="h-7 w-7 rounded-full"
                            >
                              {/* Button shows its own spinner while pending — hide the icon so they don't stack. */}
                              {!wakeAllServices.isPending && <PlugZap className="h-3.5 w-3.5" />}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Wake all services</TooltipContent>
                        </Tooltip>
                      )}
                      {/* Super-admin-only shortcut back to the system subsidiary,
                          hidden once already there. */}
                      {showSystemQuickSwitch && (
                        <Tooltip delayDuration={0}>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              isLoading={isSwitching}
                              aria-label={`Switch to ${systemTenant.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleSwitchTenant(systemTenant.id);
                              }}
                              className="h-7 w-7 rounded-full"
                            >
                              {!isSwitching && <Shield className="h-3.5 w-3.5" />}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Switch to {systemTenant.name}</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  <DropdownMenuSeparator className="my-0" />

                  {/* Search input — only rendered when the tenant list exceeds the threshold.
                    stopPropagation on keyDown prevents Radix from intercepting typing. */}
                  {showTenantSearch && (
                    <div className="px-3 py-2">
                      <div className="relative">
                        <Search
                          aria-hidden="true"
                          className="text-muted-foreground absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2"
                        />
                        <Input
                          ref={tenantSearchRef}
                          className={cn('h-8 pl-8 text-sm', tenantSearch && 'pr-8')}
                          placeholder="Search subsidiaries..."
                          value={tenantSearch}
                          onChange={(e) => {
                            setTenantSearch(e.target.value);
                          }}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                          }}
                        />
                        {/* Clear button — shown only when there's a query; clears
                            the search and returns focus to the input. */}
                        {tenantSearch && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute top-1/2 right-1 h-6 w-6 -translate-y-1/2 cursor-pointer"
                            onClick={() => {
                              setTenantSearch('');
                              tenantSearchRef.current?.focus();
                            }}
                          >
                            <X className="h-3.5 w-3.5" />
                            <span className="sr-only">Clear search</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {showTenantSearch && <DropdownMenuSeparator className="my-0" />}

                  {/* Cap the tenant list height so the popover never stretches
                    past the viewport — long lists scroll within this region. */}
                  <div className="max-h-80 overflow-y-auto px-1 py-1">
                    {filteredTenants.length === 0 ? (
                      <p className="text-muted-foreground px-2 py-4 text-center text-sm">
                        No subsidiaries found.
                      </p>
                    ) : (
                      filteredTenants.map((tenant) => {
                        const isActive = tenant.id === activeTenantId;
                        const isOrg = tenant.type === 'ORGANIZATION';
                        return (
                          <DropdownMenuItem
                            key={tenant.id}
                            disabled={isSwitching}
                            className={cn(
                              'cursor-pointer gap-3 rounded-sm px-2 py-2',
                              isActive && 'bg-selected-row',
                              isSwitching && 'cursor-not-allowed opacity-50',
                            )}
                            onClick={() => {
                              void handleSwitchTenant(tenant.id);
                            }}
                          >
                            <div
                              className={cn(
                                'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                                isActive
                                  ? 'bg-primary text-primary-foreground'
                                  : 'text-muted-foreground',
                              )}
                            >
                              {isActive ? (
                                <Check className="h-4 w-4" />
                              ) : isOrg ? (
                                <Building2 className="h-4 w-4" />
                              ) : (
                                <GitBranch className="h-4 w-4" />
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium">{tenant.name}</p>
                              <p className="text-muted-foreground truncate text-xs">
                                {tenant.slug}
                              </p>
                            </div>

                            <Badge variant="outline" className="shrink-0 text-[10px]">
                              {isOrg ? 'Org' : 'Sub'}
                            </Badge>
                          </DropdownMenuItem>
                        );
                      })
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <div className="bg-nav-badge-bg flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xs font-semibold text-white">
                    {tenantInitials}
                  </div>
                </TooltipTrigger>
                {!expanded && (
                  <TooltipContent side="right" sideOffset={8}>
                    {activeTenantName || 'Subsidiary'}
                  </TooltipContent>
                )}
              </Tooltip>
            )}
          </div>
          {/* Tenant name — revealed inline only while the rail is expanded. */}
          {expanded && (
            <div className="flex min-w-0 flex-1 items-center gap-1 pr-2">
              <div className="min-w-0 flex-1">
                <p className="text-nav-foreground truncate text-sm font-medium">
                  {activeTenantName || 'Subsidiary'}
                </p>
                {hasMultipleSubsidiaries && (
                  <p className="text-muted-foreground truncate text-xs">Switch subsidiary</p>
                )}
              </div>
              {/* Pin — only reachable once the rail is expanded, which is exactly
                  when the user has hovered in and may want it to stay. */}
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-pressed={sidebarPinned}
                    aria-label={sidebarPinned ? 'Unpin sidebar' : 'Pin sidebar open'}
                    onClick={() => {
                      setSidebarPinned(!sidebarPinned);
                    }}
                    className={cn(
                      'size-7 shrink-0 cursor-pointer hover:bg-white/60',
                      sidebarPinned ? 'text-primary' : 'text-muted-foreground',
                    )}
                  >
                    {sidebarPinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>
                  {sidebarPinned ? 'Unpin sidebar' : 'Pin sidebar open'}
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>

        {/* Primary navigation. Rows are full-width with a fixed 72px icon zone,
            so expanding the rail only reveals labels — icons never move. */}
        <nav className="flex min-h-0 flex-1 flex-col items-stretch space-y-1 overflow-y-auto py-1">
          <NavItem to="/onboarding" icon={Sparkles} label="Onboarding" collapsed={!expanded} />

          <div className={cn('py-1.5', expanded ? 'px-4' : 'flex w-18 shrink-0 justify-center')}>
            {/* Divider spans the icon column when collapsed, full width when expanded. */}
            <div className={cn(!expanded && 'w-10')}>
              <Separator />
            </div>
          </div>

          {/* Dynamic nav items from GET /screens/navigation. Always navigate to
              the group's own path (e.g. /platform) — each group with sub-items
              owns an index route that redirects to its default child, so the
              outer sidebar doesn't need to know which child is the default.
              Skip groups whose `code` is in FEATURE_GATES but whose flag is
              not enabled — same logic the inner sidebar (`nav-main.tsx`) runs.
              Platform Administration (/platform) and Configuration Management
              (/configuration) are pinned below History — filter them out here. */}
          {mainNavGroups
            ?.filter((g) => !PINNED_ADMIN_PATHS.has(g.path) && isGroupVisible(g.path, g.scope))
            .map((group) => {
              const flagKey = flagKeyForCode(group.code);
              if (flagKey !== undefined) {
                const enabledInAuth = authFlags ? authFlags[flagKey] === true : false;
                const enabledInTenant = tenantFlags ? tenantFlags[flagKey] === true : false;
                if (!enabledInAuth && !enabledInTenant) return null;
              }
              // Product Catalog is gated on connections, not a feature flag.
              if (isProductCatalogNav(group) && !hasProductCatalog) return null;
              if (group.path === '/subsidiary-preferences') {
                return (
                  <NavGroupWithSubmenu
                    key={group.id}
                    group={group}
                    GroupIcon={getIcon(group.icon)}
                    expanded={expanded}
                    items={group.items
                      .filter((item) => subsidiaryScreens.isPathEnabled(item.path))
                      .map((item) => ({
                        ...item,
                        Icon: item.icon ? getIcon(item.icon) : null,
                      }))}
                  />
                );
              }
              return (
                <NavItem
                  key={group.id}
                  to={group.path}
                  icon={getIcon(group.icon)}
                  label={group.title}
                  collapsed={!expanded}
                  activeBasePath={group.path}
                  tag={navTagFor(group.path)}
                  {...(NAV_EXTRA_ACTIVE_PATHS[group.path]
                    ? { extraActivePaths: NAV_EXTRA_ACTIVE_PATHS[group.path] }
                    : {})}
                />
              );
            })}

          <div className={cn('py-1.5', expanded ? 'px-4' : 'flex w-18 shrink-0 justify-center')}>
            {/* Divider spans the icon column when collapsed, full width when expanded. */}
            <div className={cn(!expanded && 'w-10')}>
              <Separator />
            </div>
          </div>

          {/* Platform Administration, Integrations, Explorer and Configuration
              Management sit below the divider — admin/config tools are secondary to
              the core workflow. Ordered by PINNED_ADMIN_ORDER (Integrations after
              Platform, Explorer after Integrations). */}
          {[
            ...(navigationData?.groups ?? []),
            // Fallbacks until identity serves these groups (never both).
            ...FALLBACK_PINNED_NAV.filter(
              (fallback) => !navigationData?.groups.some((g) => g.path === fallback.path),
            ),
          ]
            .filter((g) => PINNED_ADMIN_PATHS.has(g.path) && isGroupVisible(g.path, g.scope))
            .sort((a, b) => pinnedOrderIndex(a.path) - pinnedOrderIndex(b.path))
            .map((group) => {
              const flagKey = flagKeyForCode(group.code);
              if (flagKey !== undefined) {
                const enabledInAuth = authFlags ? authFlags[flagKey] === true : false;
                const enabledInTenant = tenantFlags ? tenantFlags[flagKey] === true : false;
                if (!enabledInAuth && !enabledInTenant) return null;
              }
              if (group.path === '/subsidiary-preferences') {
                return (
                  <NavGroupWithSubmenu
                    key={group.id}
                    group={group}
                    GroupIcon={getIcon(group.icon)}
                    expanded={expanded}
                    items={group.items
                      .filter((item) => subsidiaryScreens.isPathEnabled(item.path))
                      .map((item) => ({
                        ...item,
                        Icon: item.icon ? getIcon(item.icon) : null,
                      }))}
                  />
                );
              }
              return (
                <NavItem
                  key={group.id}
                  to={group.path}
                  icon={getIcon(group.icon)}
                  label={group.title}
                  collapsed={!expanded}
                  activeBasePath={group.path}
                  tag={navTagFor(group.path)}
                  {...(NAV_EXTRA_ACTIVE_PATHS[group.path]
                    ? { extraActivePaths: NAV_EXTRA_ACTIVE_PATHS[group.path] }
                    : {})}
                />
              );
            })}

          {isCostApprover && (
            <NavItem
              to="/cost-approvals"
              icon={CircleDollarSign}
              label={
                pendingCostCount > 0
                  ? `Cost approvals (${String(pendingCostCount)})`
                  : 'Cost approvals'
              }
              collapsed={!expanded}
              activeBasePath="/cost-approvals"
            />
          )}

          {/* TODO: Restore History nav item once the history view is finalised. */}
          {/* <NavItem to="/history" icon={History} label="History" collapsed /> */}
        </nav>

        {/* Notification bell */}
        <div className="flex shrink-0 items-center pb-1">
          {/* Fixed 72px zone keeps the bell in the same position when expanded. */}
          <div className="flex w-18 shrink-0 items-center justify-center">
            <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="[&_svg]:text-nav-icon relative flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl transition-colors hover:bg-white/60 focus:outline-none"
                    >
                      <Bell className="h-5 w-5" />
                      {/* Numeric badge — `yes-count` mode */}
                      {showNumericBadge && (
                        <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] leading-none font-bold text-white">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                      {/* Dot indicator — `yes-badge` mode */}
                      {showDotBadge && (
                        <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />
                      )}
                    </button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                {!expanded && (
                  <TooltipContent side="right" sideOffset={8}>
                    {unreadCount > 0
                      ? `Notifications (${String(unreadCount)} unread)`
                      : 'Notifications'}
                  </TooltipContent>
                )}
              </Tooltip>

              <DropdownMenuContent
                side="right"
                align="end"
                sideOffset={12}
                className="w-[400px] rounded-lg p-0 shadow-lg"
              >
                {/* Header */}
                <div className="border-border flex items-start justify-between border-b px-4 py-3">
                  <div>
                    <h3 className="text-foreground text-sm font-semibold">Notifications</h3>
                    {unreadCount > 0 && (
                      <p className="text-muted-foreground mt-0.5 text-xs">
                        {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={markingRead || unreadCount === 0}
                      onClick={() => {
                        markAllRead();
                      }}
                      className="hover:bg-muted inline-flex h-7 cursor-pointer items-center gap-1.5 rounded px-2 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <CheckCheck className="h-3.5 w-3.5" />
                      Mark all as read
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Notification settings"
                      onClick={handleOpenNotificationSettings}
                      className="text-primary h-7 w-7 cursor-pointer"
                    >
                      <Settings aria-hidden="true" className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Notification list */}
                <div className="max-h-[420px] overflow-y-auto">
                  {previewNotifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <Bell className="text-muted-foreground/40 mb-2 h-8 w-8" />
                      <p className="text-muted-foreground text-sm">You&apos;re all caught up</p>
                    </div>
                  ) : (
                    previewNotifications.map((n) => (
                      <NotificationRow
                        key={n.id}
                        notification={n}
                        onNavigate={handleNotificationNavigate}
                      />
                    ))
                  )}
                </div>

                {/* Footer */}
                <div className="border-border border-t">
                  <button
                    type="button"
                    onClick={handleShowAllNotifications}
                    className="text-primary hover:bg-muted/60 flex w-full cursor-pointer items-center justify-center gap-1.5 py-3 text-sm font-medium transition-colors"
                  >
                    Show all notifications
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {/* Bell label — revealed inline only while the rail is expanded. */}
          {expanded && (
            <span className="text-nav-foreground truncate pr-4 text-sm font-medium">
              Notifications
            </span>
          )}
        </div>

        {/* User avatar + dropdown at bottom */}
        <div className="flex shrink-0 items-center py-1">
          {/* Fixed 72px zone keeps the avatar in the same position when expanded. */}
          <div className="flex w-18 shrink-0 items-center justify-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 cursor-pointer rounded-full focus:outline-none"
                >
                  <Avatar className="h-9 w-9">
                    {user?.avatarUrl ? (
                      <AvatarImage src={user.avatarUrl} alt={user.displayName} />
                    ) : null}
                    <AvatarFallback className="text-xs">
                      {user?.displayName
                        ? user.displayName
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2)
                        : ''}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="right"
                align="end"
                sideOffset={12}
                className="w-64 rounded-xl p-0"
              >
                {/* User info header */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <Avatar className="h-10 w-10 shrink-0">
                    {user?.avatarUrl ? (
                      <AvatarImage src={user.avatarUrl} alt={user.displayName} />
                    ) : null}
                    <AvatarFallback className="text-xs">
                      {user?.displayName
                        ? user.displayName
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .toUpperCase()
                            .slice(0, 2)
                        : ''}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{user?.displayName}</p>
                    <p className="text-muted-foreground truncate text-xs">{user?.email}</p>
                  </div>
                </div>

                <DropdownMenuSeparator className="my-0" />

                {/* Actions */}
                <div className="p-1.5">
                  <DropdownMenuItem
                    className="cursor-pointer gap-3 rounded-lg px-3 py-2.5"
                    onClick={() => {
                      void navigate({ to: '/settings' });
                    }}
                  >
                    <Settings className="text-nav-icon h-4 w-4" />
                    <span className="text-sm">Settings</span>
                  </DropdownMenuItem>
                  {/* Personal cost display switch — renders only for viewers who may see cost. */}
                  <ShowCostMenuItem />
                </div>

                <DropdownMenuSeparator className="my-0" />

                {/* Logout */}
                <div className="p-1.5">
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive cursor-pointer gap-3 rounded-lg px-3 py-2.5"
                    disabled={loggingOut}
                    onClick={() => {
                      setLogoutDialogOpen(true);
                    }}
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="text-sm">Log out</span>
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {/* User identity — revealed inline only while the rail is expanded. */}
          {expanded && (
            <div className="min-w-0 flex-1 pr-4">
              <p className="text-nav-foreground truncate text-sm font-medium">
                {user?.displayName}
              </p>
              <p className="text-muted-foreground truncate text-xs">{user?.email}</p>
            </div>
          )}

          {/* Logout confirmation dialog */}
          <AlertDialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Log out</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to log out? You will need to sign in again to access your
                  account.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={loggingOut}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={loggingOut}
                  onClick={() => {
                    void handleLogout();
                  }}
                >
                  {loggingOut ? 'Logging out…' : 'Log out'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </aside>

      {/* Content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <ImpersonationBanner />
        <TrialModeBanner />
        <main className="flex-1 overflow-auto">
          {/* ScreenGuard enforces URL access against /screens/navigation in
              lockstep with the sidebar — see docs/route-permissions.md. */}
          <ScreenGuard>
            <Outlet />
          </ScreenGuard>
        </main>
      </div>

      {/* Small fixed-corner progress indicator for the wake-all-services
          action above — rendered here (not inside the dropdown) so it stays
          visible after the admin closes the subsidiary switcher. */}
      {wakeAllServices.isPending && (
        <div className="bg-background fixed right-4 bottom-4 z-50 w-48 rounded-lg border p-2.5 shadow-lg">
          <div className="flex items-center justify-between gap-2">
            <span className="text-foreground text-xs font-medium">Waking services</span>
            <span className="text-primary text-xs font-semibold">
              {wakeAllServices.completed}/{wakeAllServices.total}
            </span>
          </div>
          <div
            role="progressbar"
            aria-valuenow={wakeAllServices.completed}
            aria-valuemin={0}
            aria-valuemax={wakeAllServices.total}
            className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full"
            style={{ backgroundColor: 'var(--surface-progress-track)' }}
          >
            <div
              className="bg-primary h-full rounded-full transition-[width] duration-300"
              style={{
                width: `${String((wakeAllServices.completed / wakeAllServices.total) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
